import { escapeXml, buildXmlPayload, sendXmlRequest } from './core';

// ============ ITEM NESTED ENTRY CONFIGURATION ============
const ITEM_NESTED_ENTRY_TYPES = {
  'ITEMWAREHOUSEINFO': {
    parentObject: 'ITEM',
    parentKeyField: 'ITEMID',
    containerTag: 'WAREHOUSEINFO',
    entryTag: 'itemwarehouseinfo',
    includeKeyInEntry: true
  },
  'ITEMVENDOR': {
    parentObject: 'ITEM',
    parentKeyField: 'ITEMID',
    containerTag: 'VENDORINFO',
    entryTag: 'itemvendor',
    includeKeyInEntry: true
  }
};
const ITEM_NESTED_EXCLUDED_FIELDS = ['RECORDNO', 'WHENCREATED', 'WHENMODIFIED', 'CREATEDBY', 'MODIFIEDBY', 'ITEMDIMKEY', 'WAREHOUSEDIMKEY', 'VENDORKEY', 'ITEMKEY', 'UOMKEY', 'WHSE_LOCATION_KEY', 'DEFAULT_AISLE_KEY', 'DEFAULT_ROW_KEY', 'DEFAULT_BIN_KEY'];

const shouldExcludeFromItemEntry = (key) => {
  const upperKey = key.toUpperCase();
  if (ITEM_NESTED_EXCLUDED_FIELDS.includes(upperKey)) return true;
  if (ITEM_NESTED_EXCLUDED_FIELDS.some(f => upperKey.endsWith(f))) return true;
  return false;
};

// ============ DETECTION ============
export const isItemNestedEntryType = (objectName) => {
  return Object.keys(ITEM_NESTED_ENTRY_TYPES).includes(objectName?.toUpperCase());
};

export const getItemNestedConfig = (objectName) => {
  return ITEM_NESTED_ENTRY_TYPES[objectName?.toUpperCase()] || null;
};

// ============ FUNCTIONS ============
export const getItemNestedUpdateSummary = (objectName, records) => {
  const config = ITEM_NESTED_ENTRY_TYPES[objectName?.toUpperCase()];
  if (!config) return null;
  
  const grouped = {};
  const missingId = [];
  
  records.forEach((rec, idx) => {
    const itemId = rec[config.parentKeyField];
    if (!itemId) { missingId.push(idx + 2); return; }
    if (!grouped[itemId]) grouped[itemId] = 0;
    grouped[itemId]++;
  });
  
  return {
    config,
    objectName: objectName.toUpperCase(),
    items: Object.entries(grouped).map(([id, count]) => ({ id, entryCount: count })),
    missingIdRows: missingId,
    totalEntries: records.length,
    totalItems: Object.keys(grouped).length
  };
};

export const buildItemNestedPreviewXml = (objectName, records) => {
  const config = ITEM_NESTED_ENTRY_TYPES[objectName?.toUpperCase()];
  if (!config) return '';
  
  const grouped = {};
  records.forEach(rec => {
    const id = rec[config.parentKeyField];
    if (!id) return;
    if (!grouped[id]) grouped[id] = [];
    grouped[id].push(rec);
  });
  
  const itemIds = Object.keys(grouped);
  if (itemIds.length === 0) return `<!-- No valid records with ${config.parentKeyField} -->`;
  
  const firstId = itemIds[0];
  const firstGroupRecords = grouped[firstId];
  
  const sampleEntries = firstGroupRecords.slice(0, 2);
  const entriesXml = sampleEntries.map(entry => {
    const entryFields = Object.entries(entry)
      .filter(([k, v]) => !shouldExcludeFromItemEntry(k) && v !== '' && v !== undefined && v !== null)
      .map(([k, v]) => `<${k}>${escapeXml(String(v))}</${k}>`)
      .join('\n          ');
    return `<${config.entryTag}>\n          ${entryFields}\n        </${config.entryTag}>`;
  }).join('\n        ');
  
  const moreEntriesNote = firstGroupRecords.length > 2 ? `\n        <!-- ... and ${firstGroupRecords.length - 2} more entries ... -->` : '';
  const moreItemsNote = itemIds.length > 1 ? `\n\n<!-- ... and ${itemIds.length - 1} more item(s) will be updated ... -->` : '';
  
  return `<function controlid="${escapeXml(firstId)}">
  <update>
    <${config.parentObject}>
      <${config.parentKeyField}>${escapeXml(firstId)}</${config.parentKeyField}>
      <${config.containerTag}>
        ${entriesXml}${moreEntriesNote}
      </${config.containerTag}>
    </${config.parentObject}>
  </update>
</function>${moreItemsNote}`;
};

export const writeItemNestedEntries = async (config, objectName, records) => {
  const nestedConfig = ITEM_NESTED_ENTRY_TYPES[objectName?.toUpperCase()];
  if (!nestedConfig) throw new Error(`Unknown item nested entry type: ${objectName}`);
  
  const missingId = records.filter(r => !r[nestedConfig.parentKeyField]);
  if (missingId.length > 0) throw new Error(`${missingId.length} record(s) missing ${nestedConfig.parentKeyField}.`);
  
  const grouped = {};
  records.forEach(rec => {
    const id = rec[nestedConfig.parentKeyField];
    if (!grouped[id]) grouped[id] = [];
    grouped[id].push(rec);
  });
  
  const functions = Object.entries(grouped).map(([itemId, entries]) => {
    const entriesXml = entries.map(entry => {
      const entryFields = {};
      Object.entries(entry).forEach(([key, val]) => {
        if (shouldExcludeFromItemEntry(key)) return;
        if (val === '' || val === undefined || val === null) return;
        entryFields[key] = val;
      });
      const fieldsXml = Object.entries(entryFields).map(([k, v]) => `<${k}>${escapeXml(String(v))}</${k}>`).join('');
      return `<${nestedConfig.entryTag}>${fieldsXml}</${nestedConfig.entryTag}>`;
    }).join('');
    
    return `<function controlid="${escapeXml(String(itemId))}"><update><${nestedConfig.parentObject}><${nestedConfig.parentKeyField}>${escapeXml(String(itemId))}</${nestedConfig.parentKeyField}><${nestedConfig.containerTag}>${entriesXml}</${nestedConfig.containerTag}></${nestedConfig.parentObject}></update></function>`;
  }).join('');
  
  const xmlPayload = buildXmlPayload(config, functions);
  const result = await sendXmlRequest(config, nestedConfig.parentObject, xmlPayload, 'ITEM NESTED UPDATE');
  return result.results;
};
